import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  input,
  signal,
  computed,
  TemplateRef,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';

import { ContactCardStateService } from '../contact-info-on-hover/contact-info-on-hover.directive';
import { CardDataType } from '../contact-info-types';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';

@Component({
  selector: 'glxy-contact-info-card',
  imports: [
    CommonModule,
    RouterModule,
    OverlayModule,
    GalaxyAvatarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatCardModule,
    ClipboardModule,
    GalaxyTooltip,
  ],
  templateUrl: './contact-info-card.component.html',
  styleUrls: ['./contact-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactInfoCardComponent {
  @HostBinding('class') class = 'glxy-contact-info-card';
  @HostBinding('class.is-popover') get isPopover() {
    return this.cardIsPopover();
  }

  readonly cardIsPopover = input(false, { transform: booleanAttribute });
  readonly cardData = input.required<CardDataType>();
  readonly showDefaultActions = input(true, { transform: booleanAttribute });
  readonly actionsRef = input<TemplateRef<unknown>>();

  private router = inject(Router);
  private stateService = inject(ContactCardStateService);

  // Hover state for popover
  popoverIsHovered = signal(false);

  // Copy state
  isCopied = signal(false);

  // Computed: full business address (HTML-escaped)
  readonly fullBusinessAddress = computed(() => {
    const cardData = this.cardData();
    if (!cardData?.businessAddress) return undefined;
    const addy = cardData.businessAddress;
    let address = '';
    if (addy.address1) address += addy.address1 + ', ';
    if (addy.address2) address += addy.address2 + ', ';
    if (addy.city) address += addy.city + ', ';
    if (addy.state) address += addy.state;
    if (addy.country || addy.zip) address += '\n';
    if (addy.country) address += addy.country + ', ';
    if (addy.zip) address += addy.zip;
    address = address.trim();
    if (address.endsWith(',')) address = address.slice(0, -1);
    return address.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  });

  @HostListener('mouseenter') onMouseEnter(): void {
    this.popoverIsHovered.set(true);
  }
  @HostListener('mouseleave') onMouseLeave(): void {
    this.popoverIsHovered.set(false);
  }

  handleProfileButtonClick() {
    const cardData = this.cardData();
    if (cardData?.profileButtonAction) {
      cardData.profileButtonAction();
      return;
    }
    if (cardData?.profileButtonUrlUseRouterLink) {
      if (Array.isArray(cardData.profileButtonUrl)) {
        this.router.navigate(cardData.profileButtonUrl);
      } else {
        this.router.navigate([cardData.profileButtonUrl]);
      }
    }
    // If the link is a normal link, we handle this in the template with the [href] attribute
  }

  copyClicked() {
    this.isCopied.set(true);
    window.setTimeout(() => {
      this.isCopied.set(false);
    }, 3000);
  }
}
